import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { Faq } from '../../../models/faq.model';
import { FaqGroup } from '../../../models/faq-group.model';
import { Platform } from '../../../models/platform.model';

import { FaqGroupService } from '../../../services/faq-group.service';
import { FaqService } from '../../../services/faq.service';
import { PlatformService } from '../../../services/platform.service';

import { FaqGroupModalComponent } from '../../../components/faq-group-modal/faq-group-modal.component';
import { FaqModalComponent } from '../../../components/faq-modal/faq-modal.component';
import { PlatformModalComponent } from '../../../components/platform-modal/platform-modal.component';

@Component({
  selector: 'app-platform-page',
  templateUrl: './platform-page.component.html',
  styleUrl: './platform-page.component.scss'
})
export class PlatformPageComponent {

  public platformId: string = '';
  public platform: Platform | null = null;

  /* Estado propio de la página, NO el overlay global: el spinnerService solo
     debe salir en las acciones de guardado. Aquí el render es progresivo y por
     pieza (encabezado, resumen, tarjetas), con esqueletos que reservan el alto
     final para que nada salte al llegar la respuesta. */
  public loading: boolean = true;

  /* Hueco conocido del patrón de survey-page: si el GET falla la pantalla se
     queda con el spinner girando para siempre. Aquí se resuelve con un estado
     explícito dentro de la misma página. */
  public notFound: boolean = false;

  /* Tarjetas fantasma del esqueleto: solo le dan cuerpo al listado mientras
     loading es true. */
  public skeletonGroups: null[] = [null, null];
  public skeletonFaqs: null[] = [null, null, null];

  /* Solo se guardan los grupos COLAPSADOS: el estado por defecto es expandido,
     igual que la pantalla de Ayuda de la app, y se conserva entre recargas
     porque la llave es el faqGroupId. */
  public collapsedGroupIds: Set<string> = new Set<string>();

  /* Contadores derivados. Se calculan una sola vez en getData() y se pintan
     como texto de solo lectura: nunca dentro de un input editable. */
  public totalFaqGroups: number = 0;
  public totalFaqs: number = 0;
  public visibleFaqs: number = 0;

  /* Única región viva de la página (role="status" aria-live="polite"). Vive
     SIEMPRE en el DOM: un aria-live que aparece junto con su texto no se
     anuncia. Por aquí pasan tanto la carga como cada movimiento de orden. */
  public liveMessage: string = '';

  /* Mientras se arrastra un grupo, TODOS se ven colapsados: un grupo expandido
     con 15 preguntas mide media pantalla y arrastrarlo es inmanejable. Es un
     flag temporal y NO toca collapsedGroupIds, así que al soltar cada grupo
     recupera su estado real de expandido/colapsado. */
  public draggingFaqGroup: boolean = false;

  /* Bandera aparte para la animación de ngbCollapse. Si se apagara con el
     mismo flag anterior, al soltar todos los grupos se reabrirían con la
     transición encima; y sobre todo: mientras corre .collapsing las alturas
     cambian y el CDK calcula posiciones erróneas. Se apaga ANTES de colapsar
     y se vuelve a encender un tick DESPUÉS de restaurar el estado real. */
  public animateCollapse: boolean = true;

  /* PUT de orden en vuelo. Guardan el id de la pieza que se movió: sirven a la
     vez de candado —un solo reordenamiento por nivel, para no mandar listas
     contradictorias— y de bandera para el indicador discreto de guardado, que
     se pinta sobre esa misma pieza y nunca como overlay. */
  public reorderingFaqGroupId: string | null = null;
  public reorderingFaqId: string | null = null;

  constructor(
    private activatedRoute: ActivatedRoute,
    private faqGroupService: FaqGroupService,
    private faqService: FaqService,
    private modalService: NgbModal,
    private platformService: PlatformService,
  ) {

    this.activatedRoute.params.subscribe(
      async params => {

        this.platformId = params['platformId'];

        this.getData();

      }
    );

  }

  /*
  ██████╗ ███████╗██╗     ███████╗████████╗███████╗███████╗ █████╗  ██████╗
  ██╔══██╗██╔════╝██║     ██╔════╝╚══██╔══╝██╔════╝██╔════╝██╔══██╗██╔═══██╗
  ██║  ██║█████╗  ██║     █████╗     ██║   █████╗  █████╗  ███████║██║   ██║
  ██║  ██║██╔══╝  ██║     ██╔══╝     ██║   ██╔══╝  ██╔══╝  ██╔══██║██║▄▄ ██║
  ██████╔╝███████╗███████╗███████╗   ██║   ███████╗██║     ██║  ██║╚██████╔╝
  ╚═════╝ ╚══════╝╚══════╝╚══════╝   ╚═╝   ╚══════╝╚═╝     ╚═╝  ╚═╝ ╚══▀▀═╝

  */
  async deleteFaq(
    faqGroup: FaqGroup,
    faq: Faq
  ) {

    const flag = confirm(
      `¿Está seguro que desea borrar la pregunta "${faq.question}"?`
    );

    if(flag) {

      const secondFlag = await this.faqService.delete(
        faqGroup,
        faq
      );

      if(secondFlag) {
        this.getData();
      }

    }

  }

  /*
  ██████╗ ███████╗██╗     ███████╗████████╗███████╗ ██████╗ ██████╗  ██████╗ ██╗   ██╗██████╗
  ██╔══██╗██╔════╝██║     ██╔════╝╚══██╔══╝██╔════╝██╔════╝ ██╔══██╗██╔═══██╗██║   ██║██╔══██╗
  ██║  ██║█████╗  ██║     █████╗     ██║   █████╗  ██║  ███╗██████╔╝██║   ██║██║   ██║██████╔╝
  ██║  ██║██╔══╝  ██║     ██╔══╝     ██║   ██╔══╝  ██║   ██║██╔══██╗██║   ██║██║   ██║██╔═══╝
  ██████╔╝███████╗███████╗███████╗   ██║   ███████╗╚██████╔╝██║  ██║╚██████╔╝╚██████╔╝██║
  ╚═════╝ ╚══════╝╚══════╝╚══════╝   ╚═╝   ╚══════╝ ╚═════╝ ╚═╝  ╚═╝ ╚═════╝  ╚═════╝ ╚═╝

  */
  async deleteFaqGroup(
    faqGroup: FaqGroup
  ) {

    const message = faqGroup.faqs.length > 0
      ? `¿Está seguro que desea borrar el grupo "${faqGroup.title}" y sus ${faqGroup.faqs.length} preguntas?`
      : `¿Está seguro que desea borrar el grupo "${faqGroup.title}"?`;

    const flag = confirm(message);

    if(flag) {

      const secondFlag = await this.faqGroupService.delete(
        this.platform!,
        faqGroup
      );

      if(secondFlag) {
        this.getData();
      }

    }

  }

  /*
  ██████╗ ██████╗  ██████╗ ██████╗ ███████╗ █████╗  ██████╗
  ██╔══██╗██╔══██╗██╔═══██╗██╔══██╗██╔════╝██╔══██╗██╔═══██╗
  ██║  ██║██████╔╝██║   ██║██████╔╝█████╗  ███████║██║   ██║
  ██║  ██║██╔══██╗██║   ██║██╔═══╝ ██╔══╝  ██╔══██║██║▄▄ ██║
  ██████╔╝██║  ██║╚██████╔╝██║     ██║     ██║  ██║╚██████╔╝
  ╚═════╝ ╚═╝  ╚═╝ ╚═════╝ ╚═╝     ╚═╝     ╚═╝  ╚═╝ ╚══▀▀═╝

  */
  /* Soltar un arrastre y picar subir/bajar terminan en la MISMA función de
     mover: un solo camino, un solo lugar donde se persiste. */
  dropFaq(
    faqGroup: FaqGroup,
    event: CdkDragDrop<Faq[]>
  ) {

    this.moveFaq(
      faqGroup,
      event.previousIndex,
      event.currentIndex
    );

  }

  /*
  ██████╗ ██████╗  ██████╗ ██████╗  ██████╗ ██████╗  ██████╗ ██╗   ██╗██████╗
  ██╔══██╗██╔══██╗██╔═══██╗██╔══██╗██╔════╝ ██╔══██╗██╔═══██╗██║   ██║██╔══██╗
  ██║  ██║██████╔╝██║   ██║██████╔╝██║  ███╗██████╔╝██║   ██║██║   ██║██████╔╝
  ██║  ██║██╔══██╗██║   ██║██╔═══╝ ██║   ██║██╔══██╗██║   ██║██║   ██║██╔═══╝
  ██████╔╝██║  ██║╚██████╔╝██║     ╚██████╔╝██║  ██║╚██████╔╝╚██████╔╝██║
  ╚═════╝ ╚═╝  ╚═╝ ╚═════╝ ╚═╝      ╚═════╝ ╚═╝  ╚═╝ ╚═════╝  ╚═════╝ ╚═╝

  */
  dropFaqGroup(
    event: CdkDragDrop<FaqGroup[]>
  ) {

    this.moveFaqGroup(
      event.previousIndex,
      event.currentIndex
    );

  }

  /*
  ███████╗███╗   ██╗██████╗ ██████╗ ██████╗  █████╗  ██████╗
  ██╔════╝████╗  ██║██╔══██╗██╔══██╗██╔══██╗██╔══██╗██╔════╝
  █████╗  ██╔██╗ ██║██║  ██║██║  ██║██████╔╝███████║██║  ███╗
  ██╔══╝  ██║╚██╗██║██║  ██║██║  ██║██╔══██╗██╔══██║██║   ██║
  ███████╗██║ ╚████║██████╔╝██████╔╝██║  ██║██║  ██║╚██████╔╝
  ╚══════╝╚═╝  ╚═══╝╚═════╝ ╚═════╝ ╚═╝  ╚═╝╚═╝  ╚═╝ ╚═════╝

  */
  /* Se escucha en document y no solo en (cdkDragEnded) porque el usuario puede
     picar el asa y soltar sin llegar a arrastrar: ahí el CDK no emite nada y
     los grupos se quedarían colapsados. */
  @HostListener('document:pointercancel')
  @HostListener('document:pointerup')
  endFaqGroupDrag() {

    if(!this.draggingFaqGroup) { return; }

    this.draggingFaqGroup = false;

    /* La animación sigue apagada en este ciclo de detección para que la
       reapertura sea instantánea; se reactiva un tick después, ya solo para
       los toggles normales del usuario. */
    setTimeout(
      () => { this.animateCollapse = true; },
      0
    );

  }

  /*
   ██████╗ ███████╗████████╗██████╗  █████╗ ████████╗ █████╗
  ██╔════╝ ██╔════╝╚══██╔══╝██╔══██╗██╔══██╗╚══██╔══╝██╔══██╗
  ██║  ███╗█████╗     ██║   ██║  ██║███████║   ██║   ███████║
  ██║   ██║██╔══╝     ██║   ██║  ██║██╔══██║   ██║   ██╔══██║
  ╚██████╔╝███████╗   ██║   ██████╔╝██║  ██║   ██║   ██║  ██║
   ╚═════╝ ╚══════╝   ╚═╝   ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚═╝  ╚═╝

  */
  async getData() {

    this.loading = true;
    this.notFound = false;
    this.liveMessage = 'Cargando la plataforma';

    /* Un solo GET trae el árbol completo: plataforma -> grupos -> preguntas.
       No se piden los hijos por separado. */
    this.platform = await this.platformService.get(
      this.platformId
    );

    if(this.platform) {

      this.totalFaqGroups = this.platform.faqGroups.length;

      this.totalFaqs = this.platform.faqGroups.reduce(
        (total, faqGroup) => total + faqGroup.faqs.length,
        0
      );

      /* Una pregunta llega al residente SOLO si ella y su grupo están activos. */
      this.visibleFaqs = this.platform.faqGroups.reduce(
        (total, faqGroup) => faqGroup.status
          ? total + faqGroup.faqs.filter(faq => faq.status).length
          : total,
        0
      );

    } else {

      this.notFound = true;

      this.totalFaqGroups = 0;
      this.totalFaqs = 0;
      this.visibleFaqs = 0;

    }

    this.loading = false;
    this.liveMessage = '';

  }

  /*
  ██╗███████╗ ██████╗ ██████╗ ██╗     ██╗      █████╗ ██████╗ ███████╗███████╗██████╗
  ██║██╔════╝██╔════╝██╔═══██╗██║     ██║     ██╔══██╗██╔══██╗██╔════╝██╔════╝██╔══██╗
  ██║███████╗██║     ██║   ██║██║     ██║     ███████║██████╔╝███████╗█████╗  ██║  ██║
  ██║╚════██║██║     ██║   ██║██║     ██║     ██╔══██║██╔═══╝ ╚════██║██╔══╝  ██║  ██║
  ██║███████║╚██████╗╚██████╔╝███████╗███████╗██║  ██║██║     ███████║███████╗██████╔╝
  ╚═╝╚══════╝ ╚═════╝ ╚═════╝ ╚══════╝╚══════╝╚═╝  ╚═╝╚═╝     ╚══════╝╚══════╝╚═════╝

  */
  isCollapsed(
    faqGroup: FaqGroup
  ): boolean {
    /* draggingFaqGroup manda mientras dura el arrastre, pero no se escribe en
       collapsedGroupIds: el estado persistido queda intacto. */
    return this.draggingFaqGroup || this.collapsedGroupIds.has(faqGroup.faqGroupId);
  }

  /*
  ███╗   ███╗ ██████╗ ██╗   ██╗███████╗███████╗ █████╗  ██████╗
  ████╗ ████║██╔═══██╗██║   ██║██╔════╝██╔════╝██╔══██╗██╔═══██╗
  ██╔████╔██║██║   ██║██║   ██║█████╗  █████╗  ███████║██║   ██║
  ██║╚██╔╝██║██║   ██║╚██╗ ██╔╝██╔══╝  ██╔══╝  ██╔══██║██║▄▄ ██║
  ██║ ╚═╝ ██║╚██████╔╝ ╚████╔╝ ███████╗██║     ██║  ██║╚██████╔╝
  ╚═╝     ╚═╝ ╚═════╝   ╚═══╝  ╚══════╝╚═╝     ╚═╝  ╚═╝ ╚══▀▀═╝

  */
  /* Único punto de movimiento de preguntas: lo usan el arrastre y los botones
     de subir/bajar. Una pregunta jamás cambia de grupo — el backend lo
     rechaza — y por eso cada grupo tiene su propia zona, sin conectar. */
  moveFaq(
    faqGroup: FaqGroup,
    previousIndex: number,
    currentIndex: number,
    focusTarget: HTMLElement | null = null
  ) {

    const faqs = faqGroup.faqs;

    /* Un PUT de preguntas a la vez: dos listas en vuelo dejarían guardado el
       orden de la que responda al último. */
    if(this.reorderingFaqId) { return; }

    if(previousIndex < 0 || previousIndex >= faqs.length) { return; }

    const faq = faqs[previousIndex];

    /* Ya es la primera / ya es la última: la acción es imposible, no inválida.
       El botón sigue existiendo y sigue siendo enfocable; aquí solo se explica
       por qué no pasó nada. */
    if(currentIndex < 0) {
      this.liveMessage = `La pregunta «${faq.question}» ya está en la primera posición del grupo «${faqGroup.title}».`;
      return;
    }

    if(currentIndex >= faqs.length) {
      this.liveMessage = `La pregunta «${faq.question}» ya está en la última posición del grupo «${faqGroup.title}».`;
      return;
    }

    if(previousIndex === currentIndex) { return; }

    /* Copia del orden anterior ANTES de mover: es exactamente lo que se
       restaura si el PUT falla. */
    const previousOrder = [...faqs];

    /* Optimista: la lista se reacomoda de inmediato y el PUT sale detrás. */
    moveItemInArray(faqs, previousIndex, currentIndex);

    this.liveMessage = `La pregunta «${faq.question}» ahora está en la posición ${currentIndex + 1} de ${faqs.length} del grupo «${faqGroup.title}».`;

    this.returnFocus(focusTarget);

    this.persistFaqOrder(faqGroup, faq, previousOrder);

  }

  /*
  ███╗   ███╗ ██████╗ ██╗   ██╗███████╗ ██████╗ ██████╗  ██████╗ ██╗   ██╗██████╗
  ████╗ ████║██╔═══██╗██║   ██║██╔════╝██╔════╝ ██╔══██╗██╔═══██╗██║   ██║██╔══██╗
  ██╔████╔██║██║   ██║██║   ██║█████╗  ██║  ███╗██████╔╝██║   ██║██║   ██║██████╔╝
  ██║╚██╔╝██║██║   ██║╚██╗ ██╔╝██╔══╝  ██║   ██║██╔══██╗██║   ██║██║   ██║██╔═══╝
  ██║ ╚═╝ ██║╚██████╔╝ ╚████╔╝ ███████╗╚██████╔╝██║  ██║╚██████╔╝╚██████╔╝██║
  ╚═╝     ╚═╝ ╚═════╝   ╚═══╝  ╚══════╝ ╚═════╝ ╚═╝  ╚═╝ ╚═════╝  ╚═════╝ ╚═╝

  */
  moveFaqGroup(
    previousIndex: number,
    currentIndex: number,
    focusTarget: HTMLElement | null = null
  ) {

    if(!this.platform) { return; }

    const faqGroups = this.platform.faqGroups;

    if(this.reorderingFaqGroupId) { return; }

    if(previousIndex < 0 || previousIndex >= faqGroups.length) { return; }

    const faqGroup = faqGroups[previousIndex];

    if(currentIndex < 0) {
      this.liveMessage = `El grupo «${faqGroup.title}» ya está en la primera posición.`;
      return;
    }

    if(currentIndex >= faqGroups.length) {
      this.liveMessage = `El grupo «${faqGroup.title}» ya está en la última posición.`;
      return;
    }

    if(previousIndex === currentIndex) { return; }

    const previousOrder = [...faqGroups];

    moveItemInArray(faqGroups, previousIndex, currentIndex);

    this.liveMessage = `El grupo «${faqGroup.title}» ahora está en la posición ${currentIndex + 1} de ${faqGroups.length}.`;

    this.returnFocus(focusTarget);

    this.persistFaqGroupOrder(faqGroup, previousOrder);

  }

  /*
   ██████╗ ██████╗ ███████╗███╗   ██╗███████╗ █████╗  ██████╗
  ██╔═══██╗██╔══██╗██╔════╝████╗  ██║██╔════╝██╔══██╗██╔═══██╗
  ██║   ██║██████╔╝█████╗  ██╔██╗ ██║█████╗  ███████║██║   ██║
  ██║   ██║██╔═══╝ ██╔══╝  ██║╚██╗██║██╔══╝  ██╔══██║██║▄▄ ██║
  ╚██████╔╝██║     ███████╗██║ ╚████║██║     ██║  ██║╚██████╔╝
   ╚═════╝ ╚═╝     ╚══════╝╚═╝  ╚═══╝╚═╝     ╚═╝  ╚═╝ ╚══▀▀═╝

  */
  openFaq(
    faqGroup: FaqGroup,
    faq?: Faq
  ) {

    const modalRef = this.modalService.open(
      FaqModalComponent,
      { size: 'lg' }
    );

    modalRef.componentInstance.faqGroup = faqGroup;
    modalRef.componentInstance.faq = faq;

    modalRef.closed.subscribe(
      async () => {

        this.getData();

      }
    );

  }

  /*
   ██████╗ ██████╗ ███████╗███╗   ██╗ ██████╗ ██████╗  ██████╗ ██╗   ██╗██████╗
  ██╔═══██╗██╔══██╗██╔════╝████╗  ██║██╔════╝ ██╔══██╗██╔═══██╗██║   ██║██╔══██╗
  ██║   ██║██████╔╝█████╗  ██╔██╗ ██║██║  ███╗██████╔╝██║   ██║██║   ██║██████╔╝
  ██║   ██║██╔═══╝ ██╔══╝  ██║╚██╗██║██║   ██║██╔══██╗██║   ██║██║   ██║██╔═══╝
  ╚██████╔╝██║     ███████╗██║ ╚████║╚██████╔╝██║  ██║╚██████╔╝╚██████╔╝██║
   ╚═════╝ ╚═╝     ╚══════╝╚═╝  ╚═══╝ ╚═════╝ ╚═╝  ╚═╝ ╚═════╝  ╚═════╝ ╚═╝

  */
  openFaqGroup(
    faqGroup?: FaqGroup
  ) {

    const modalRef = this.modalService.open(
      FaqGroupModalComponent
    );

    modalRef.componentInstance.platform = this.platform;
    modalRef.componentInstance.faqGroup = faqGroup;

    modalRef.closed.subscribe(
      async () => {

        this.getData();

      }
    );

  }

  /*
   ██████╗ ██████╗ ███████╗███╗   ██╗██████╗ ██╗      █████╗ ████████╗
  ██╔═══██╗██╔══██╗██╔════╝████╗  ██║██╔══██╗██║     ██╔══██╗╚══██╔══╝
  ██║   ██║██████╔╝█████╗  ██╔██╗ ██║██████╔╝██║     ███████║   ██║
  ██║   ██║██╔═══╝ ██╔══╝  ██║╚██╗██║██╔═══╝ ██║     ██╔══██║   ██║
  ╚██████╔╝██║     ███████╗██║ ╚████║██║     ███████╗██║  ██║   ██║
   ╚═════╝ ╚═╝     ╚══════╝╚═╝  ╚═══╝╚═╝     ╚══════╝╚═╝  ╚═╝   ╚═╝

  */
  openPlatform() {

    /* Se reutiliza tal cual el modal del listado: no se duplica el formulario
       de plataforma. */
    const modalRef = this.modalService.open(
      PlatformModalComponent
    );

    modalRef.componentInstance.platform = this.platform;

    modalRef.closed.subscribe(
      async () => {

        this.getData();

      }
    );

  }

  /*
  ██████╗ ███████╗██████╗ ███████╗██╗███████╗████████╗ ██████╗ ██████╗  ██████╗ ██╗   ██╗██████╗
  ██╔══██╗██╔════╝██╔══██╗██╔════╝██║██╔════╝╚══██╔══╝██╔════╝ ██╔══██╗██╔═══██╗██║   ██║██╔══██╗
  ██████╔╝█████╗  ██████╔╝███████╗██║███████╗   ██║   ██║  ███╗██████╔╝██║   ██║██║   ██║██████╔╝
  ██╔═══╝ ██╔══╝  ██╔══██╗╚════██║██║╚════██║   ██║   ██║   ██║██╔══██╗██║   ██║██║   ██║██╔═══╝
  ██║     ███████╗██║  ██║███████║██║███████║   ██║   ╚██████╔╝██║  ██║╚██████╔╝╚██████╔╝██║
  ╚═╝     ╚══════╝╚═╝  ╚═╝╚══════╝╚═╝╚══════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝ ╚═════╝  ╚═════╝ ╚═╝

  */
  private async persistFaqGroupOrder(
    faqGroup: FaqGroup,
    previousOrder: FaqGroup[]
  ) {

    const platform = this.platform!;

    /* El id de la pieza movida hace de candado —un solo PUT de grupos a la
       vez— y de bandera del indicador discreto, que se pinta sobre ese mismo
       grupo y no como overlay. */
    this.reorderingFaqGroupId = faqGroup.faqGroupId;

    const faqGroups = await this.faqGroupService.updateOrder(
      platform,
      platform.faqGroups.map(item => item.faqGroupId)
    );

    /* Si mientras el PUT viajaba se recargó la pantalla, este resultado ya no
       le corresponde a lo que se está viendo. */
    if(this.platform !== platform) {
      this.reorderingFaqGroupId = null;
      return;
    }

    if(faqGroups) {

      /* Se refresca SOLO el sortOrder con lo que devolvió el endpoint. No se
         reemplazan los objetos —la respuesta no trae las preguntas de cada
         grupo y sustituirlos las borraría de la pantalla— y no se llama
         getData(), que cambia this.platform completo y hace parpadear todo. */
      const sortOrders = new Map(
        faqGroups.map(item => [item.faqGroupId, item.sortOrder])
      );

      platform.faqGroups.forEach(
        item => {

          const sortOrder = sortOrders.get(item.faqGroupId);

          if(sortOrder !== undefined) {
            item.sortOrder = sortOrder;
          }

        }
      );

    } else {

      /* Falló: la pantalla no se queda mintiendo. El mensaje del backend ya
         salió en un toast desde el servicio. */
      platform.faqGroups = previousOrder;
      this.liveMessage = 'No se pudo guardar el orden de los grupos. Se restauró el orden anterior.';

    }

    this.reorderingFaqGroupId = null;

  }

  /*
  ██████╗ ███████╗██████╗ ███████╗██╗███████╗████████╗███████╗ █████╗  ██████╗
  ██╔══██╗██╔════╝██╔══██╗██╔════╝██║██╔════╝╚══██╔══╝██╔════╝██╔══██╗██╔═══██╗
  ██████╔╝█████╗  ██████╔╝███████╗██║███████╗   ██║   █████╗  ███████║██║   ██║
  ██╔═══╝ ██╔══╝  ██╔══██╗╚════██║██║╚════██║   ██║   ██╔══╝  ██╔══██║██║▄▄ ██║
  ██║     ███████╗██║  ██║███████║██║███████║   ██║   ██║     ██║  ██║╚██████╔╝
  ╚═╝     ╚══════╝╚═╝  ╚═╝╚══════╝╚═╝╚══════╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚══▀▀═╝

  */
  private async persistFaqOrder(
    faqGroup: FaqGroup,
    faq: Faq,
    previousOrder: Faq[]
  ) {

    const platform = this.platform!;

    this.reorderingFaqId = faq.faqId;

    const faqs = await this.faqService.updateOrder(
      faqGroup,
      faqGroup.faqs.map(item => item.faqId)
    );

    if(this.platform !== platform) {
      this.reorderingFaqId = null;
      return;
    }

    if(faqs) {

      /* Igual que con los grupos: solo se refresca el sortOrder de los objetos
         que ya están en pantalla. Nada de getData(). */
      const sortOrders = new Map(
        faqs.map(item => [item.faqId, item.sortOrder])
      );

      faqGroup.faqs.forEach(
        item => {

          const sortOrder = sortOrders.get(item.faqId);

          if(sortOrder !== undefined) {
            item.sortOrder = sortOrder;
          }

        }
      );

    } else {

      /* Falló: la pantalla no se queda mintiendo. El mensaje del backend ya
         salió en un toast desde el servicio. */
      faqGroup.faqs = previousOrder;
      this.liveMessage = `No se pudo guardar el orden de las preguntas del grupo «${faqGroup.title}». Se restauró el orden anterior.`;

    }

    this.reorderingFaqId = null;

  }

  /*
  ██████╗ ███████╗████████╗██╗   ██╗██████╗ ███╗   ██╗███████╗ ██████╗  ██████╗██╗   ██╗███████╗
  ██╔══██╗██╔════╝╚══██╔══╝██║   ██║██╔══██╗████╗  ██║██╔════╝██╔═══██╗██╔════╝██║   ██║██╔════╝
  ██████╔╝█████╗     ██║   ██║   ██║██████╔╝██╔██╗ ██║█████╗  ██║   ██║██║     ██║   ██║███████╗
  ██╔══██╗██╔══╝     ██║   ██║   ██║██╔══██╗██║╚██╗██║██╔══╝  ██║   ██║██║     ██║   ██║╚════██║
  ██║  ██║███████╗   ██║   ╚██████╔╝██║  ██║██║ ╚████║██║     ╚██████╔╝╚██████╗╚██████╔╝███████║
  ╚═╝  ╚═╝╚══════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝╚═╝  ╚═══╝╚═╝      ╚═════╝  ╚═════╝ ╚═════╝ ╚══════╝

  */
  /* El @for lleva track por id, así que el DOM se mueve en vez de recrearse y
     el botón picado sobrevive. Aun así varios navegadores desenfocan un nodo
     al reinsertarlo, así que el foco se devuelve a mano: los botones de
     subir/bajar son el ÚNICO camino por teclado y perder el foco ahí obliga a
     tabular desde cero en cada movimiento. */
  private returnFocus(
    focusTarget: HTMLElement | null
  ) {

    if(!focusTarget) { return; }

    setTimeout(
      () => { focusTarget.focus(); },
      0
    );

  }

  /*
  ███████╗████████╗ █████╗ ██████╗ ████████╗██████╗ ██████╗  █████╗  ██████╗
  ██╔════╝╚══██╔══╝██╔══██╗██╔══██╗╚══██╔══╝██╔══██╗██╔══██╗██╔══██╗██╔════╝
  ███████╗   ██║   ███████║██████╔╝   ██║   ██║  ██║██████╔╝███████║██║  ███╗
  ╚════██║   ██║   ██╔══██║██╔══██╗   ██║   ██║  ██║██╔══██╗██╔══██║██║   ██║
  ███████║   ██║   ██║  ██║██║  ██║   ██║   ██████╔╝██║  ██║██║  ██║╚██████╔╝
  ╚══════╝   ╚═╝   ╚═╝  ╚═╝╚═╝  ╚═╝   ╚═╝   ╚═════╝ ╚═╝  ╚═╝╚═╝  ╚═╝ ╚═════╝

  */
  /* Se colapsa en el pointerdown del asa y NO en (cdkDragStarted): para cuando
     el CDK emite ese evento ya midió la lista, y cambiar las alturas después
     le da posiciones erróneas. Aquí el alto queda estable —y sin transición,
     por animateCollapse— antes del primer pointermove, que es cuando el CDK
     arranca el arrastre de verdad. */
  startFaqGroupDrag() {

    if(this.reorderingFaqGroupId) { return; }

    this.animateCollapse = false;
    this.draggingFaqGroup = true;

  }

  /*
  ████████╗ ██████╗  ██████╗  ██████╗ ██╗     ███████╗
  ╚══██╔══╝██╔═══██╗██╔════╝ ██╔════╝ ██║     ██╔════╝
     ██║   ██║   ██║██║  ███╗██║  ███╗██║     █████╗
     ██║   ██║   ██║██║   ██║██║   ██║██║     ██╔══╝
     ██║   ╚██████╔╝╚██████╔╝╚██████╔╝███████╗███████╗
     ╚═╝    ╚═════╝  ╚═════╝  ╚═════╝ ╚══════╝╚══════╝

  */
  toggleGroup(
    faqGroup: FaqGroup
  ) {

    if(this.collapsedGroupIds.has(faqGroup.faqGroupId)) {
      this.collapsedGroupIds.delete(faqGroup.faqGroupId);
    } else {
      this.collapsedGroupIds.add(faqGroup.faqGroupId);
    }

  }

}
